#!/usr/bin/env node
/**
 * Synkro CLI bootstrap.
 *
 * Loads .env, then dispatches to the right subcommand.
 */
import { readFileSync, existsSync } from 'node:fs';
import { resolve } from 'node:path';

const envCandidates = [
  resolve(process.env.HOME ?? '', '.synkro', 'config.env'),
];
for (const envPath of envCandidates) {
  if (!existsSync(envPath)) continue;
  const envContent = readFileSync(envPath, 'utf-8');
  for (const line of envContent.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const eqIndex = trimmed.indexOf('=');
    if (eqIndex <= 0) continue;
    const key = trimmed.slice(0, eqIndex).trim();
    const value = trimmed.slice(eqIndex + 1).trim().replace(/^['"]|['"]$/g, '');
    if (!process.env[key] && !value.startsWith('op://')) process.env[key] = value;
  }
}

const args = process.argv.slice(2);
const cmd = args[0] || '';
const subArgs = args.slice(1);

function printVersion() {
  console.log(__SYNKRO_CLI_VERSION__);
}

function printHelp() {
  console.log(`Synkro CLI — runtime safety for AI coding agents

Usage:
  synkro <command> [options]

Commands:
  install [--force]      Install or update Synkro
  uninstall [--purge]    Remove Synkro (keeps scan data; --purge wipes that too)
  stop                   Gracefully stop the server (snapshot + checkpoint)
  start [opts]           Start the server (with pgdata integrity check)
  restart [opts]         Safe restart (stop → start, data preserved)
  update                 Pull the latest container image and safely restart
  config                 Show or change grading + storage modes
  claude-desktop         Monitor Claude Desktop conversations (local, macOS)
  version                Show version

  config:
    synkro config                        show current settings
    synkro config grading <local|byok>   where grading runs
    synkro config storage <local|cloud>  where telemetry is stored

  start/restart opts (recreate the worker pool):
    --workers N          total grader workers (default 8, even-split)
    --providers a,b      grading agents: claude, cursor (or both)
    e.g. synkro restart --workers 16 --providers claude,cursor

Quick start:
  $ synkro install       # one-time setup
  $ claude               # use Claude Code normally; Synkro judges in real time
`);
}

async function main() {
  switch (cmd) {
    case 'install': {
      const { installCommand, parseArgs } = await import('./commands/install.js');
      await installCommand(parseArgs(subArgs));
      break;
    }
    case 'uninstall':
    case 'disconnect': {
      const { disconnectCommand } = await import('./commands/disconnect.js');
      disconnectCommand(subArgs);
      break;
    }
    case 'login': {
      const { authenticate, isAuthenticated } = await import('./auth/stub.js');
      if (isAuthenticated()) {
        console.log('Already authenticated.');
      } else {
        console.log('Opening browser for Synkro auth...');
        const result = await authenticate((status) => {
          if (status.phase === 'success') console.log('  ✓ Authenticated');
          else if (status.phase === 'error') console.error('  ✗ ' + status.message);
        });
        if (!result) { console.error('Authentication failed.'); process.exit(1); }
      }
      break;
    }
    case 'grade': {
      const { gradeCommand } = await import('./commands/grade.js');
      await gradeCommand(subArgs);
      break;
    }
    case 'local-cc': {
      const { localCcCommand } = await import('./commands/localCc.js');
      await localCcCommand(subArgs);
      break;
    }
    case 'import': {
      const { importCommand } = await import('./commands/import.js');
      await importCommand();
      break;
    }
    case 'sync': {
      const { syncCommand } = await import('./commands/sync.js');
      await syncCommand(subArgs);
      break;
    }
    case 'version':
    case '--version':
    case '-v': {
      printVersion();
      break;
    }
    case 'help':
    case '--help':
    case '-h':
    case '': {
      printHelp();
      break;
    }
    case 'stop': {
      const { stopCommand } = await import('./commands/lifecycle.js');
      await stopCommand();
      break;
    }
    case 'start': {
      const { startCommand } = await import('./commands/lifecycle.js');
      await startCommand(args.slice(1));
      break;
    }
    case 'restart': {
      const { restartCommand } = await import('./commands/lifecycle.js');
      await restartCommand(args.slice(1));
      break;
    }
    case 'update': {
      const { updateCommand } = await import('./commands/lifecycle.js');
      await updateCommand();
      break;
    }
    case 'config': {
      const { configCommand } = await import('./commands/config.js');
      await configCommand(args.slice(1));
      break;
    }
    case 'claude-desktop': {
      // Start a Claude Desktop observability session against the already-running
      // container — no reinstall, no image pull. (The same tap is also offered
      // during `synkro install`.) Runs until Ctrl+C.
      const { runClaudeDesktopTap } = await import('./installer/claudeDesktopTap.js');
      // History backfill is opt-in: `synkro claude-desktop --backfill` imports past
      // conversations once. Without it, only live turns are captured.
      await runClaudeDesktopTap({ backfill: subArgs.includes('--backfill') });
      break;
    }
    default: {
      console.error(`Unknown command: ${cmd}`);
      printHelp();
      process.exit(1);
    }
  }
}

main().catch((err) => {
  console.error(err);
  process.exit(1);
});
